// @givmo/board — a copy of the return, on the director's own page (CBG-YR5-DIST's web half, frame
// p6; the backend's `board-return-distribution.md` §8.5–§8.6), at `/director/returns/$recordId?org=`:
// the address the copy's mail spells whole (`BOARD_DIRECTOR_RETURN_PATH`). Change both sides in one
// round, never one.
//
// ⚠ "OPEN THE RETURN" IS THE RECEIPT. The file's link is fetched only when the person presses it,
// never prefetched or warmed: a recipient's first fetch is what Board records as their opening (§8.6),
// so a link fetched on load would record an opening that didn't happen.
//
// What the page says is the person's own: when they opened it, and the shipped "Record that I’ve read
// this" with its approved words. Nothing about anyone else — no recipients, no counts, no receipts.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { firstErrorCode, isGivmoError } from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  ErrorMessage,
  Icon,
  Skeleton,
  testIdProp,
} from '@givmo/design-system';
import { ExternalLink, FileText } from 'lucide-react';
import { Link, useParams } from '../../board-routing';
import { BOARD_DIRECTOR_PAGES } from '../../board-routes';
import { useBoardDirectorOperations } from '../../board-runtime';
import {
  directorBoardIdentity,
  directorBoardSearch,
  useDirectorBoardAddress,
} from '../../director/director-board';
import { boardDirectorMeKey } from '../../director/director-me';
import { DIRECTOR_REFUSALS, isCommitteeOnly } from '../../director/director-wire';
import { DirectorReadRefusal, useDirectorReadsMayFire } from '../../director/read-gate';
import { DirectorReadFailed, useDirectorFailureWords } from '../../director/access-refusal';
import { displayClockTime, displayLongDate } from '../../lib/display';
import { oneLine, shortDay } from '../conflicts/coi-decode';
import { onboardingWords as gsWords } from '../onboarding/onboarding-words';
import { decodeDirectorCopy, longDate, type DirectorReturnCopy } from '../returns/return-decode';
import { returnWords as w } from '../returns/return-words';
import { DirectorActRow, DirectorSetupOfferPage, useDirectorAct } from './director-act';
import { DirectorBoardContent } from './director-board-content';
import { DirectorBoardOnlyPage } from './director-board-only-page';
import { DirectorShell } from './director-shell';

const BACK_LINK =
  'font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline';
const PLAIN = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
const QUIET = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary';

/** The director's copy page's query key, under the director family's own root. */
export function directorReturnKey(orgRef: string | null, recordId: string) {
  return ['board-director-return', orgRef ?? '', recordId] as const;
}

export function DirectorReturnPage() {
  const { board } = useDirectorBoardAddress();
  // CBG-YR6-OUTSIDE (review-215's F5): A copy of the return is the board's alone; a seat for someone who isn't
  // on the board, here by address, meets the one not-found and nothing of the board's.
  if (isCommitteeOnly(board)) return <DirectorBoardOnlyPage testId="page-director-return" />;
  return <DirectorReturnPageForTheBoard />;
}

function DirectorReturnPageForTheBoard() {
  const { recordId } = useParams({ from: '/director/returns/$recordId' });
  const queryClient = useQueryClient();
  const operations = useBoardDirectorOperations();
  const { orgRef, board, notYours } = useDirectorBoardAddress();
  const mayFire = useDirectorReadsMayFire(orgRef);
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  // CBG-FIX104: the access refusal in the words of the seat `/me` listed here.
  const failureWords = useDirectorFailureWords();
  const key = directorReturnKey(orgRef, recordId);

  const copyQuery = useQuery({
    queryKey: key,
    enabled: orgRef !== null && mayFire,
    retry: false,
    queryFn: async (): Promise<DirectorReturnCopy | null> => {
      const { data } = await operations.directorGetBoardReturnCopyForDirector({
        path: { org_ref: orgRef ?? '', record_id: recordId },
        throwOnError: true,
      });
      return decodeDirectorCopy(data);
    },
  });
  const copy = copyQuery.data ?? null;

  // Only on the press: the first fetch IS the receipt (see the header).
  const open = useMutation({
    mutationFn: async () => {
      const { data } = await operations.directorOpenBoardReturnCopyForDirector({
        path: { org_ref: orgRef ?? '', record_id: recordId },
        throwOnError: true,
      });
      return data;
    },
    onMutate: () => setOpenError(null),
    onSuccess: async (link) => {
      window.open(link.download_url, '_blank', 'noopener,noreferrer');
      const openedAt = typeof link.opened_at === 'string' ? link.opened_at : null;
      if (copy !== null && openedAt !== null && copy.opened_at === null) {
        queryClient.setQueryData(key, { ...copy, opened_at: openedAt });
      }
      // The home's row goes once the person has opened it.
      await queryClient.invalidateQueries({ queryKey: ['board-director-attention', orgRef] });
    },
    onError: (caught) => {
      setOpenError(failureWords(caught, w.openFailed));
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });

  const acknowledge = useDirectorAct('record_acknowledgment', {
    onDone: async () => {
      setAcknowledged(true);
      await queryClient.invalidateQueries({ queryKey: key });
      await queryClient.invalidateQueries({ queryKey: ['board-director-attention', orgRef] });
    },
    onFailed: (caught) => {
      if (isGivmoError(caught) && firstErrorCode(caught) === DIRECTOR_REFUSALS.intentChanged) {
        setError('The words above changed. Read them, then try again.');
        void queryClient.invalidateQueries({ queryKey: boardDirectorMeKey() });
        return;
      }
      if (isGivmoError(caught) && firstErrorCode(caught) === 'board_record_already_acknowledged') {
        // Recorded already (a second tab, a double press): the act stands once, and says so.
        setAcknowledged(true);
        return;
      }
      setError(failureWords(caught, 'We couldn’t record that. Please try again.'));
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });

  const identity = directorBoardIdentity(board);
  const back = (
    <Link
      to={BOARD_DIRECTOR_PAGES.records}
      search={orgRef === null ? undefined : directorBoardSearch(orgRef)}
      // Exact: the records list is not the current page.
      exact
      className={BACK_LINK}
      {...testIdProp('director-return-back')}
    >
      {w.minuteBook}
    </Link>
  );

  if (acknowledge.offering) {
    return (
      <DirectorSetupOfferPage
        variant="from-sign"
        identity={identity}
        onSettled={acknowledge.settleOffer}
      />
    );
  }

  if (orgRef === null || notYours || copyQuery.isPending || copyQuery.isError || copy === null) {
    return (
      <DirectorShell
        title={w.directorPageTitle}
        identity={identity}
        actions={notYours ? undefined : back}
        testId="page-director-return"
      >
        <DirectorBoardContent orgRef={orgRef} notYours={notYours}>
          {copyQuery.isPending ? (
            <Skeleton className="h-90 w-full rounded-lg" testId="director-return-loading" />
          ) : null}
          <DirectorReadRefusal reads={[copyQuery]} testId="director-return-read-step-up" />
          {copyQuery.isError && isGivmoError(copyQuery.error) && copyQuery.error.status === 404 ? (
            <Card testId="director-return-not-found">
              <CardBody>
                <p className={PLAIN}>{w.notFound}</p>
              </CardBody>
            </Card>
          ) : copyQuery.isError ? (
            <DirectorReadFailed
              read={copyQuery}
              line={w.directorLoadFailed}
              testId="director-return-error"
              retryTestId="director-return-retry"
            />
          ) : null}
        </DirectorBoardContent>
      </DirectorShell>
    );
  }

  const withdrawn = copy.withdrawn !== null;
  const confirmed =
    copy.acknowledgment === null
      ? null
      : copy.acknowledgment.road === 'paper'
        ? gsWords.paperConfirmedOn(
            shortDay(copy.acknowledgment.paper_signed_on ?? copy.acknowledgment.acknowledged_at),
          )
        : gsWords.youConfirmedOn(shortDay(copy.acknowledgment.acknowledged_at));
  const openedDay = copy.opened_at === null ? null : displayLongDate(copy.opened_at);

  const beginAcknowledge = () => {
    setError(null);
    acknowledge.begin(
      (intentStatementId) => () =>
        operations.directorAcknowledgeBoardRecord({
          path: { org_ref: orgRef, record_id: copy.record_id },
          // The echo is what makes the act name the exact bytes this copy's record binds.
          body: { content_hash: copy.content_hash, intent_statement_id: intentStatementId },
          throwOnError: true,
        }),
    );
  };

  return (
    <DirectorShell
      title={w.directorTitle(copy.tax_year.named_year, copy.form)}
      description={w.providedToTheBoard(copy.provided_by_display_name, longDate(copy.provided_on))}
      identity={identity}
      actions={back}
      testId="page-director-return"
    >
      {withdrawn ? (
        <Card testId="director-return-withdrawn">
          <CardBody className="flex flex-col gap-6">
            <p className={PLAIN}>{w.directorWithdrawn}</p>
            {copy.withdrawn !== null && copy.withdrawn.reason.length > 0 ? (
              <p className={QUIET}>“{copy.withdrawn.reason}”</p>
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      {copy.newer_copy_record_id !== null ? (
        <p className={PLAIN} data-testid="director-return-newer">
          {w.newerCopyProvided}{' '}
          <Link
            to="/director/returns/$recordId"
            params={{ recordId: copy.newer_copy_record_id }}
            search={directorBoardSearch(orgRef)}
            className={BACK_LINK}
          >
            {w.openNewerCopy}
          </Link>
        </p>
      ) : null}

      <Card testId="director-return-file">
        <CardBody className="flex flex-col gap-16">
          <div className="flex flex-wrap items-center justify-between gap-12">
            <div className="flex min-w-0 items-start gap-12">
              <Icon
                icon={FileText}
                size={20}
                aria-hidden
                className="mt-2 shrink-0 text-text-tertiary"
              />
              <span className="flex min-w-0 flex-col gap-2">
                <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong [overflow-wrap:anywhere]">
                  {copy.file.file_name}
                </span>
                <span className={QUIET}>{w.pdfPages(copy.file.page_count)}</span>
              </span>
            </div>
            {withdrawn ? null : (
              <Button
                appearance="saas"
                tone="secondary"
                loading={open.isPending}
                onClick={() => open.mutate()}
                testId="director-return-open"
              >
                <span className="flex items-center gap-6">
                  {w.openTheReturn}
                  <Icon icon={ExternalLink} size={14} aria-hidden />
                </span>
              </Button>
            )}
          </div>
          {openedDay !== null && copy.opened_at !== null ? (
            <p className={PLAIN} data-testid="director-return-opened">
              {w.openedOn(oneLine(openedDay), displayClockTime(copy.opened_at))}
            </p>
          ) : null}
          {!copy.is_recipient ? (
            <p className={QUIET} data-testid="director-return-not-recipient">
              {w.notARecipient}
            </p>
          ) : null}
          {openError !== null ? (
            <ErrorMessage testId="director-return-open-error">{openError}</ErrorMessage>
          ) : null}
        </CardBody>
      </Card>

      {acknowledged ? (
        <p
          className="font-sans text-s14 font-w600 leading-h15 tracking-givmo text-text-strong"
          {...testIdProp('director-return-acknowledged')}
        >
          {w.thankYou}
        </p>
      ) : confirmed !== null ? (
        <p
          className="font-sans text-s14 font-w600 leading-h15 tracking-givmo text-text-strong"
          {...testIdProp('director-return-confirmed')}
        >
          {confirmed}
        </p>
      ) : (
        <Card testId="director-return-actions">
          <CardBody className="flex flex-col gap-12">
            <p className={PLAIN}>{w.ifYouveRead}</p>
            <DirectorActRow
              state={acknowledge}
              label={w.recordThatIveRead}
              onPress={beginAcknowledge}
              error={error}
              testIdPrefix="director-return"
              buttonTestId="director-return-acknowledge"
            />
          </CardBody>
        </Card>
      )}

      <DirectorReadRefusal reads={[copyQuery]} testId="director-return-read-step-up" />
      {acknowledge.dialog}
    </DirectorShell>
  );
}
